import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { get2faStatus, setup2faAuthenticator, confirm2faAuthenticator, enable2faEmail, disable2fa } from '@/lib/api';
import { Shield, Smartphone, Mail, Check, Loader2, AlertCircle, X, Lock, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function SecurityPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetupAuth, setShowSetupAuth] = useState(false);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const loadStatus = () => {
    get2faStatus().then(setStatus).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { loadStatus(); }, []);

  const handleEnableEmail = async () => {
    setEnabling(true);
    try { await enable2faEmail(); loadStatus(); }
    catch (err) { console.error(err); }
    finally { setEnabling(false); }
  };

  const handleDisable = async () => {
    try { await disable2fa(); setShowConfirmDisable(false); loadStatus(); }
    catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  const method = status?.tfa_method || '';
  const enabledAt = status?.tfa_enabled_at;

  return (
    <div className="animate-fade-in" data-testid="security-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Security</h1>
        <p className="text-sm text-[#5C5C55] mt-1">Manage two-factor authentication and account security</p>
      </div>

      {/* Current status */}
      <div className={`rounded-2xl p-5 mb-6 flex items-center gap-4 ${method ? 'bg-[#E8EDE5] border border-[#D0D9CB]' : 'bg-[#FDFAF0] border border-[#EDE4C8]'}`} data-testid="tfa-status">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${method ? 'bg-[#3A4035]' : 'bg-[#F8F0D8]'}`}>
          <Shield className={`w-5 h-5 ${method ? 'text-white' : 'text-[#8A7230]'}`} strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#1A1A1A]">
            {method ? 'Two-factor authentication is enabled' : 'Two-factor authentication is not enabled'}
          </p>
          <p className="text-xs text-[#5C5C55] mt-0.5">
            {method === 'authenticator' ? 'Using authenticator app' : method === 'email' ? 'Using email verification codes' : 'Enable 2FA to add an extra layer of security to your account'}
          </p>
          {enabledAt && <p className="text-[10px] text-[#8A8B85] mt-1">Enabled {new Date(enabledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
        </div>
        {method && (
          <button onClick={() => setShowConfirmDisable(true)} className="text-[11px] px-3 py-1.5 rounded-full font-medium text-[#B33A3A] border border-[#F0D4D4] bg-white hover:bg-[#FDF2F2] transition-colors" data-testid="disable-2fa-btn">
            Disable
          </button>
        )}
      </div>

      {/* Setup options */}
      {!method && (
        <div className="space-y-4" data-testid="tfa-setup-options">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Choose a method</p>

          {/* Authenticator App */}
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#E8EDE5] flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-1">Authenticator App</h3>
              <p className="text-xs text-[#5C5C55] leading-relaxed mb-3">Use Google Authenticator, Microsoft Authenticator, or any TOTP app. Most secure option — works offline.</p>
              <Button onClick={() => setShowSetupAuth(true)} className="bg-[#3A4035] text-white rounded-full px-5 py-2 text-xs hover:bg-[#2B3027]" data-testid="setup-authenticator-btn">
                Set up authenticator
              </Button>
            </div>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#E8EDE5] text-[#3A4035] font-semibold shrink-0">Recommended</span>
          </div>

          {/* Email */}
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#F2F0EB] flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#5C5C55]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-1">Email Verification</h3>
              <p className="text-xs text-[#5C5C55] leading-relaxed mb-3">Receive a 6-digit code to your email ({user?.email}) each time you log in.</p>
              <Button onClick={handleEnableEmail} disabled={enabling} variant="outline" className="rounded-full px-5 py-2 text-xs border-[#E5E2DC]" data-testid="enable-email-2fa-btn">
                {enabling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}Enable email 2FA
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Already enabled — show current method details */}
      {method && (
        <div className="space-y-4">
          <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Active Method</p>
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#E8EDE5] flex items-center justify-center shrink-0">
              {method === 'authenticator' ? <Smartphone className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} /> : <Mail className="w-5 h-5 text-[#3A4035]" strokeWidth={1.5} />}
            </div>
            <div>
              <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-1">
                {method === 'authenticator' ? 'Authenticator App' : 'Email Verification'}
              </h3>
              <p className="text-xs text-[#5C5C55] leading-relaxed">
                {method === 'authenticator' ? 'A 6-digit code from your authenticator app is required on every login.' : `A 6-digit code is sent to ${user?.email} on every login.`}
              </p>
            </div>
          </div>

          {/* Switch method */}
          <p className="text-xs text-[#8A8B85]">To switch methods, disable 2FA first, then enable the new method.</p>
        </div>
      )}

      {/* Security info */}
      <div className="mt-8 space-y-3">
        <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Account Security</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5 flex items-center gap-3">
            <Lock className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-medium text-[#1A1A1A]">Password strength</p>
              <p className="text-[10px] text-[#8A8B85]">Min 8 chars, uppercase, lowercase, number</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5 flex items-center gap-3">
            <Clock className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
            <div>
              <p className="text-xs font-medium text-[#1A1A1A]">Session timeout</p>
              <p className="text-[10px] text-[#8A8B85]">Auto-logout after 4 hours of inactivity</p>
            </div>
          </div>
        </div>
      </div>

      {/* Setup Authenticator Modal */}
      <SetupAuthenticatorModal open={showSetupAuth} onClose={() => setShowSetupAuth(false)} onEnabled={() => { setShowSetupAuth(false); loadStatus(); }} />

      {/* Confirm Disable Modal */}
      {showConfirmDisable && (
        <Dialog open onOpenChange={() => setShowConfirmDisable(false)}>
          <DialogContent className="sm:max-w-sm bg-white rounded-2xl border-[#E5E2DC]" data-testid="confirm-disable-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Disable Two-Factor Authentication</DialogTitle>
              <DialogDescription className="text-xs text-[#5C5C55]">Your account will be less secure without 2FA.</DialogDescription>
            </DialogHeader>
            <div className="bg-[#FDF2F2] rounded-xl p-3 border border-[#F0D4D4] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
              <p className="text-xs text-[#B33A3A]">Anyone with your password will be able to access your account.</p>
            </div>
            <div className="flex gap-3 pt-1">
              <Button variant="outline" onClick={() => setShowConfirmDisable(false)} className="flex-1 rounded-full h-10 border-[#E5E2DC]">Cancel</Button>
              <Button onClick={handleDisable} className="flex-1 bg-[#B33A3A] text-white rounded-full h-10 hover:bg-[#8A2D2D]" data-testid="confirm-disable-btn">Disable 2FA</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SetupAuthenticatorModal({ open, onClose, onEnabled }) {
  const [step, setStep] = useState(1);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStep(1); setCode(''); setError(''); setLoading(true);
      setup2faAuthenticator()
        .then(d => { setQrCode(d.qr_code); setSecret(d.secret); })
        .catch(err => setError('Failed to generate QR code'))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleConfirm = async (e) => {
    if (e) e.preventDefault();
    if (code.length < 6) return;
    setError(''); setLoading(true);
    try {
      await confirm2faAuthenticator(code);
      onEnabled();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code — check your authenticator app and try again');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="setup-auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Set Up Authenticator</DialogTitle>
          <DialogDescription className="text-xs text-[#5C5C55]">
            {step === 1 ? 'Scan the QR code with your authenticator app' : 'Enter the 6-digit code to confirm'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]" data-testid="auth-setup-error">
            <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">{error}</p>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>
            ) : (
              <>
                <div className="bg-white border border-[#E5E2DC] rounded-xl p-4 flex justify-center">
                  {qrCode && <img src={qrCode} alt="QR Code" className="w-48 h-48" data-testid="qr-code-img" />}
                </div>
                <div className="bg-[#F2F0EB] rounded-xl p-3">
                  <p className="text-[10px] text-[#8A8B85] mb-1">Can't scan? Enter this code manually:</p>
                  <p className="text-xs font-mono text-[#1A1A1A] break-all" data-testid="totp-secret">{secret}</p>
                </div>
                <p className="text-xs text-[#5C5C55] leading-relaxed">
                  1. Open Google Authenticator or Microsoft Authenticator<br />
                  2. Tap the + button<br />
                  3. Scan the QR code above
                </p>
                <Button onClick={() => setStep(2)} className="w-full bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="auth-next-step">
                  I've scanned the code
                </Button>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleConfirm} className="space-y-4">
            <p className="text-xs text-[#5C5C55]">Enter the 6-digit code shown in your authenticator app to confirm setup.</p>
            <div>
              <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Verification Code</Label>
              <Input value={code} onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                placeholder="000000" maxLength={6} autoFocus
                className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl text-2xl text-center tracking-[0.5em] font-heading h-14 placeholder:text-[#D8DBD3]"
                data-testid="auth-confirm-code" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full h-10 border-[#E5E2DC]">Back</Button>
              <Button type="submit" disabled={loading || code.length < 6} className="flex-1 bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="auth-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />}Confirm
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
